import { Controller } from '@hotwired/stimulus'

// Command palette (ux-redesign F11, CORE-033): Ctrl/⌘+K or „/“ opens the <dialog>; typing reloads the results
// frame from the server after 300 ms; ↑/↓ move the highlighted option (combobox + aria-activedescendant) and Enter
// opens it (Q-REV-20). Enter without an option submits the form → full results page. „N“ opens Nová zakázka (UX S1)
// by clicking the „+ Nový“ menu link (present only when the user may create orders); a list with its own N button (hotkeys_controller) handles it there.
export default class CommandPaletteController extends Controller<HTMLElement> {
  static override targets = ['dialog', 'input', 'frame', 'newOrder']
  static override values = { url: String }
  declare readonly dialogTarget: HTMLDialogElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly frameTarget: HTMLElement
  declare readonly urlValue: string
  declare readonly newOrderTarget: HTMLAnchorElement
  declare readonly hasNewOrderTarget: boolean

  private timer: number | undefined
  private active = -1

  override disconnect(): void {
    window.clearTimeout(this.timer)
  }

  hotkey(event: KeyboardEvent): void {
    const modK = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k'
    const plain = !event.ctrlKey && !event.metaKey && !event.altKey && !this.editable(event.target) && !this.overlayOpen()
    const slash = event.key === '/' && plain
    if (event.key.toLowerCase() === 'n' && !event.shiftKey && plain && this.hasNewOrderTarget &&
      !document.querySelector('[data-hotkey="n"]')) {
      event.preventDefault()
      this.newOrderTarget.click()
      return
    }
    if (!modK && !slash) return

    event.preventDefault()
    if (modK && this.dialogTarget.open) this.close()
    else this.open()
  }

  open(): void {
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    this.inputTarget.select()
    if (!this.frameTarget.hasAttribute('src')) this.load()
  }

  close(): void {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  // Until the new results arrive nothing is highlighted, so Enter can't open a stale row.
  search(): void {
    this.highlight(-1)
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.load(), 300)
  }

  // New results: the first option is highlighted so Enter opens it right away.
  loaded(): void {
    this.highlight(0)
  }

  navigate(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      this.highlight(this.active + (event.key === 'ArrowDown' ? 1 : -1))
    } else if (event.key === 'Enter') {
      const option = this.options[this.active]
      if (!option) return

      event.preventDefault()
      option.click()
    }
  }

  private load(): void {
    window.clearTimeout(this.timer)
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('q', this.inputTarget.value)
    this.frameTarget.setAttribute('src', url.toString())
  }

  private get options(): HTMLAnchorElement[] {
    return Array.from(this.frameTarget.querySelectorAll<HTMLAnchorElement>('a[role="option"]'))
  }

  // -1 = back in the input (legacy: ↑ on the first row returns to the input).
  private highlight(index: number): void {
    const options = this.options
    this.active = options.length === 0 ? -1 : Math.min(Math.max(index, -1), options.length - 1)
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.active)))
    const current = options[this.active]
    if (current) {
      this.inputTarget.setAttribute('aria-activedescendant', current.id)
      current.scrollIntoView({ block: 'nearest' })
    } else {
      this.inputTarget.removeAttribute('aria-activedescendant')
    }
  }

  // A modal, a dropdown or a popover menu owns the keyboard while it is open.
  private overlayOpen(): boolean {
    return document.querySelector('dialog[open], details.dropdown[open], :popover-open') !== null
  }

  // Chrome leaves the focus in the closed palette's input when it was opened by a key; that input is not typing.
  private editable(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && !target.closest('dialog:not([open])') && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  }
}
